<template>
    <div class="siren-searchbar">
        <div class="siren-searchbar__label">
            <CompassIcon icon="wand" />
            <label class="typographies-paragraph-medium-semibold">
                {{ $t.__('SirenSearchBar.label') }}
            </label>
        </div>
        <CompassInput
            ref="sirenSearchBarInput"
            v-model="selectedValue"
            :placeholder="$t.__('SirenSearchBar.placeholder')"
            suffixIcon="search"
            @keydown.native.esc.stop.prevent="handleCancel"
        />
        <CompassDropdown
            ref="sirenSearchBarDropdown"
            v-click-outside="handleCancel"
            :isVisible="showDropdown"
            parentRef="sirenSearchBarInput"
            dropdownClass="siren-searchbar-dropdown"
            :noResultsLabel="$t.__('common.labels.no_result')"
            :minWidth="minWidth"
            :noResults="results.length === 0 && !isLoading"
        >
            <template v-if="isLoading">
                <CompassDropdownOption
                    v-for="n in 2"
                    :key="n"
                    value=""
                    label=""
                    :disabled="true"
                    class="siren-searchbar-dropdown-option--loading"
                >
                    <SirenSearchBarLoading />
                </CompassDropdownOption>
            </template>
            <CompassDropdownOption
                v-for="result in results"
                :key="result.siret"
                :label="result.name"
                :value="result.id"
                @click="handleCloseAndCreateNewTaskClick(result)"
            >
                <div class="siren-searchbar-dropdown-option">
                    <div
                        class="siren-searchbar-dropdown-option__title typographies-paragraph-medium-semibold"
                    >
                        <div class="siren-searchbar-dropdown-option__icon">
                            <CompassIcon icon="building-community" size="extra-small" />
                        </div>
                        {{ result.name }}
                    </div>
                    <div>
                        <div
                            class="siren-searchbar-dropdown-option__siret typographies-paragraph-small-regular"
                        >
                            {{ result.siret }}
                        </div>
                        <div
                            class="siren-searchbar-dropdown-option__address typographies-paragraph-small-regular"
                        >
                            {{ result.address.address_line_1 }}
                        </div>
                    </div>
                </div>
            </CompassDropdownOption>
        </CompassDropdown>
    </div>
</template>
<script>
import { companyScan } from 'api/searchEngineService';
import { debounce } from 'lodash';
import SirenSearchBarLoading from 'vcomponents/common/SirenSearchBar/SirenSearchBarLoading.vue';

export default {
    components: {
        SirenSearchBarLoading,
    },
    data() {
        return {
            isLoading: false,
            showDropdown: false,
            selectedValue: '',
            minWidth: 0,
            observer: null,
            results: [],
        };
    },
    watch: {
        selectedValue(newValue) {
            this.handleSearch(newValue);
            if (newValue === '') {
                this.showDropdown = false;
                this.results = [];
            }
        },
    },
    mounted() {
        this.initObserver();
    },
    beforeDestroy() {
        if (this.observer) this.observer.unobserve(this.$refs.sirenSearchBarInput.$el);
    },
    methods: {
        handleSearch(query) {
            if (query.length < 3) {
                this.results = [];
                this.showDropdown = false;
                this.isLoading = false;
                return;
            }
            this.isLoading = true;
            this.results = [];
            if (!this.showDropdown) {
                this.showDropdown = true;
            }
            debounce(() => {
                companyScan(this.selectedValue, 'FR')
                    .then((response) => {
                        this.results = response.data?.data ?? [];
                        this.isLoading = false;
                    })
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            message: this.$t.__('SirenSearchBar.fetch.error'),
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }, 300)();
        },
        handleCloseAndCreateNewTaskClick(result) {
            this.showDropdown = false;
            this.selectedValue = '';
            this.$emit('select', result);
        },
        initObserver() {
            const observer = new ResizeObserver(this.setDropdownMinWidth);
            observer.observe(this.$refs.sirenSearchBarInput.$el);
            this.observer = observer;
        },
        setDropdownMinWidth() {
            this.minWidth = this.$refs.sirenSearchBarInput.$el.offsetWidth;
        },
        handleCancel() {
            this.showDropdown = false;
            this.results = [];
            this.selectedValue = '';
            this.isLoading = false;
        },
    },
};
</script>
<style lang="scss" scoped>
.siren-searchbar {
    background: var(--colors-sellsy-ai-0);
    border-radius: var(--spacing-0);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
    padding: var(--spacing-3) var(--spacing-2);

    &__label {
        color: var(--colors-sellsy-ai-100);
        display: flex;
        gap: var(--spacing-1);
    }

    &-dropdown-option {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-0);

        &--loading {
            cursor: default;

            &::v-deep {
                .compass-dropdown-option__label {
                    flex: 1;
                }
            }
        }

        &__icon {
            align-items: center;
            background: var(--colors-common-background-subtle);
            border-radius: var(--spacing-0);
            color: var(--colors-common-text-subtle-default);
            display: flex;
            flex-shrink: 0;
            height: 20px;
            justify-content: center;
            width: 20px;
        }

        &__title {
            color: var(--colors-common-text-standard-default);
            display: flex;
            gap: var(--spacing-0);
        }

        &__siret,
        &__address {
            color: var(--colors-common-text-standard-caption);
        }
    }
}
</style>
